import {
  TAGS,
  BRANDS,
  AUTHORS,
  SENDERS,
  CHECKLISTS,
  ADVISOR_USERS,
  ANY_SENDERS,
  ASSIGNEE_USERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS,
} from 'constants/FilterConstants';
import type { PayloadAction } from 'modules/global/generic.types';
import { mergeDeep } from 'utils/ObjectUtils';

import { readEntityId } from './utils';

// These fields hold objects with an id field (brands: [{ id: 'brand-id', name: 'Bmw', ... }]), so
// removing one value compares ids instead of the objects themselves.
export const ID_FILTER: string[] = [
  BRANDS, AUTHORS, SENDERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, ANY_SENDERS, ASSIGNEE_USERS, CHECKLISTS,
  ADVISOR_USERS, TAGS,
];

/** One filter change: which field, and the value to set — or to pull out of a multi-value filter. */
export type FilterPayload<T> = {
  field: keyof T & string;
  value?: unknown;
};

export type FilterAdapter<T> = {
  getInitialState: () => T;
  setFilter: (state: T, action: PayloadAction<FilterPayload<T>>) => T;
  /** The payload is a LIST of changes — `setDashboardFilters(filters)` dispatches an array. */
  setFilters: (state: T, action: PayloadAction<Array<FilterPayload<T>>>) => T;
  removeFilter: (state: T, action: PayloadAction<FilterPayload<T>>) => T;
  resetFilters: () => T;
};

/**
 * Builds the handlers of a dashboard's filter slice.
 * @param initialState the empty filter set — also what a reset and a removed filter fall back to
 * @param persistedValues what `services/StateLoader` restored from localStorage, merged on top
 */
export const filterSliceAdapter = <T extends object>(initialState: T, persistedValues: Partial<T> = {}): FilterAdapter<T> => {
  // A computed key cannot be spread back onto `T` in TypeScript, hence the single assertion per
  // handler: `field` is a `keyof T`, so the result is a `T` by construction.
  const withField = (state: T, field: keyof T & string, value: unknown): T => ({ ...state, [field]: value }) as T;

  const removeFilter = (state: T, { payload: { field, value } }: PayloadAction<FilterPayload<T>>): T => {
    const current = state[field];
    const isIdFilter = ID_FILTER.includes(field);
    const newValue = Array.isArray(current)
      ? current.filter(item => (isIdFilter ? readEntityId(item) !== value : item !== value))
      : initialState[field];

    return withField(state, field, newValue);
  };

  const setFilters = (state: T, { payload }: PayloadAction<Array<FilterPayload<T>>>): T =>
    payload.reduce((acc, { field, value }) => withField(acc, field, value), state);

  const resetFilters = (): T => initialState;

  const setFilter = (state: T, { payload: { field, value } }: PayloadAction<FilterPayload<T>>): T =>
    withField(state, field, value);

  const getInitialState = (): T => mergeDeep(initialState, persistedValues);

  return {
    setFilter,
    setFilters,
    resetFilters,
    removeFilter,
    getInitialState,
  };
};
